import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Checkbox, Input, List, Modal, Select, Space, Spin, Typography, message } from 'antd';
import { useApi } from '../../hooks/use_api';

const { Text } = Typography;

// Split and merge (todo D10; backend services/split_merge.py). A split moves
// chosen member connections to a new sibling with a copy of the profile; a
// merge folds another group of the same type into this one and needs an admin
// of each. Lineage is recorded either way and grants no reach.

export function SplitModal({ open, onClose, entity, connections, onDone }) {
    const api = useApi();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [chosen, setChosen] = useState([]);
    const [busy, setBusy] = useState(false);
    const members = (connections || []).filter(c => c.role !== 'installed' && !c.entity.attributes?.is_dm);

    useEffect(() => { if (open) { setName(`${entity.name} (2)`); setChosen([]); } }, [open, entity.name]);

    const split = () => {
        setBusy(true);
        api.post(`/api/entities/${entity.id}/split`, { name: name.trim(), connection_ids: chosen })
            .then(daughter => { message.success(`Split into ${daughter.name}`); onDone?.(); onClose(); navigate(`/entity/${daughter.id}`); })
            .catch(err => message.error(err.message || 'Could not split'))
            .finally(() => setBusy(false));
    };

    return (
        <Modal title={`Split ${entity.name}`} open={open} onCancel={onClose} onOk={split} okText="Split"
            okButtonProps={{ disabled: !name.trim() || chosen.length === 0, loading: busy }}>
            <Text type="secondary">The ones you choose move to a new {entity.entity_type} with a copy of this profile. Apps and the channel's history stay here.</Text>
            <Input style={{ margin: '12px 0' }} value={name} onChange={e => setName(e.target.value)} maxLength={255} />
            <Checkbox.Group style={{ width: '100%' }} value={chosen} onChange={setChosen}>
                <Space direction="vertical">
                    {members.map(c => (
                        <Checkbox key={c.connection_id} value={c.connection_id}>
                            {c.entity.name} <Text type="secondary">{c.role === 'admin' ? 'admin' : c.entity.entity_type}</Text>
                        </Checkbox>
                    ))}
                </Space>
            </Checkbox.Group>
        </Modal>
    );
}

export function MergeModal({ open, onClose, entity, onDone }) {
    const api = useApi();
    const [options, setOptions] = useState([]);
    const [other, setOther] = useState(null);
    const [busy, setBusy] = useState(false);

    const search = q => {
        if (!q || q.trim().length < 2) { setOptions([]); return; }
        api.post('/api/entities/search', { query: q, limit: 12, vector_weight: 0.5, text_weight: 0.5 })
            .then(rs => setOptions(rs.filter(r => r.entity_type === entity.entity_type && r.id !== entity.id)
                .map(r => ({ value: r.id, label: r.name }))))
            .catch(() => setOptions([]));
    };

    const ask = () => {
        setBusy(true);
        api.post(`/api/entities/${entity.id}/merge-requests`, { other_id: other })
            .then(r => {
                message.success(r.status === 'approved' ? 'Merged' : 'Asked their admins to approve');
                onDone?.(); onClose();
            })
            .catch(err => message.error(err.message || 'Could not ask to merge'))
            .finally(() => setBusy(false));
    };

    return (
        <Modal title={`Merge another ${entity.entity_type} into ${entity.name}`} open={open} onCancel={onClose}
            onOk={ask} okText="Merge" okButtonProps={{ disabled: !other, loading: busy }}>
            <Text type="secondary">Its members and apps join this one, it is archived with its history, and its description is used here only if this one has none. An admin of the other side approves, unless you administer both.</Text>
            <Select showSearch filterOption={false} onSearch={search} options={options} value={other} onChange={setOther}
                placeholder={`Search ${entity.entity_type}s…`} style={{ width: '100%', marginTop: 12 }} notFoundContent="Type to search" />
        </Modal>
    );
}

export function MergeRequestsBanner({ entity, isAdmin, onDone }) {
    const api = useApi();
    const [requests, setRequests] = useState(null);
    const [names, setNames] = useState({});

    const load = useCallback(() => {
        if (!isAdmin) return;
        api.get(`/api/entities/${entity.id}/merge-requests`)
            .then(rs => {
                setRequests(rs);
                const ids = [...new Set(rs.flatMap(r => [r.into_id, r.from_id]))].filter(id => id !== entity.id);
                return Promise.all(ids.map(id => api.get(`/api/entities/${id}`).catch(() => null)));
            })
            .then(es => setNames(Object.fromEntries((es || []).filter(Boolean).map(e => [e.id, e.name]))))
            .catch(() => setRequests([]));
    }, [api, entity.id, isAdmin]);

    useEffect(() => { load(); }, [load]);

    const decide = (r, how) => api.post(`/api/entities/merge-requests/${r.id}/${how}`)
        .then(() => { message.success(how === 'approve' ? 'Merged' : 'Refused'); load(); onDone?.(); })
        .catch(err => message.error(err.message || 'Could not do that'));

    if (!isAdmin || requests === null) return requests === null && isAdmin ? <Spin size="small" /> : null;
    if (requests.length === 0) return null;
    return (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Merge requests"
            description={
                <List size="small" dataSource={requests} renderItem={r => {
                    const incoming = r.from_id === entity.id;
                    const otherName = names[incoming ? r.into_id : r.from_id] || `#${incoming ? r.into_id : r.from_id}`;
                    return (
                        <List.Item actions={incoming
                            ? [<Button key="a" size="small" type="primary" onClick={() => decide(r, 'approve')}>Approve</Button>,
                               <Button key="r" size="small" onClick={() => decide(r, 'refuse')}>Refuse</Button>]
                            : [<Button key="r" size="small" onClick={() => decide(r, 'refuse')}>Withdraw</Button>]}>
                            {incoming ? `${otherName} asks to absorb this ${entity.entity_type}` : `Waiting for ${otherName}'s admins to approve merging it in`}
                        </List.Item>
                    );
                }} />
            } />
    );
}

// Use proposals (todo D13; backend services/use.py): faded members proposed
// for a pause, a faded group proposed for archive. Nothing happens until an
// admin presses a button; scores themselves are never shown.
export function UseProposals({ entity, isAdmin, connections, onDone }) {
    const api = useApi();
    const [proposals, setProposals] = useState(null);

    const load = useCallback(() => {
        if (!isAdmin) return;
        api.get(`/api/entities/${entity.id}/proposals`).then(setProposals).catch(() => setProposals(null));
    }, [api, entity.id, isAdmin]);

    useEffect(() => { load(); }, [load]);

    if (!proposals) return null;
    const faded = (connections || []).filter(c => proposals.pause_connection_ids.includes(c.connection_id));
    if (faded.length === 0 && !proposals.archive) return null;

    const pause = c => {
        const until = new Date(Date.now() + 30 * 86400000).toISOString();
        api.post(`/api/connections/${c.connection_id}/pause`, { until })
            .then(() => { message.success(`Paused ${c.entity.name} for 30 days`); load(); onDone?.(); })
            .catch(err => message.error(err.message || 'Could not pause'));
    };

    return (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="Use suggests"
            description={<>
                {faded.length > 0 && <List size="small" dataSource={faded} renderItem={c => (
                    <List.Item actions={[<Button key="p" size="small" onClick={() => pause(c)}>Pause 30 days</Button>]}>
                        {c.entity.name} has not been around for a while
                    </List.Item>
                )} />}
                {proposals.archive && <Text>This {entity.entity_type} has been quiet for a long time. Archive it from its settings if it is done.</Text>}
            </>} />
    );
}
